import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, TrendingUp, Eye, MousePointer, Target, 
  DollarSign, Edit, Trash2, Play, Pause, CheckCircle 
} from 'lucide-react';

const AdminCampaignDashboardNew = () => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, analyticsRes] = await Promise.all([
        apiCall(`/api/campaigns/${activeTab !== 'all' ? `?status=${activeTab}` : ''}`),
        apiCall('/api/campaigns/analytics/overview')
      ]);

      if (campaignsRes.success) setCampaigns(campaignsRes.campaigns);
      if (analyticsRes.success) setAnalytics(analyticsRes.analytics);
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const updateCampaignStatus = async (campaignId, status) => {
    try {
      const response = await apiCall('PUT', `/campaigns/${campaignId}`, { status });
      if (response.success) {
        toast({ title: 'Success', description: `Campaign ${status}` });
        fetchData();
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      const response = await apiCall('DELETE', `/campaigns/${campaignId}`);
      if (response.success) {
        toast({ title: 'Success', description: 'Campaign deleted' });
        fetchData();
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const stats = [
    {
      title: 'Total Campaigns',
      value: analytics?.total_campaigns || 0,
      icon: Target,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      title: 'Total Budget',
      value: `₹${analytics?.total_budget?.toLocaleString('en-IN') || '0'}`,
      icon: DollarSign,
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    {
      title: 'Total Impressions',
      value: analytics?.total_impressions?.toLocaleString() || '0',
      icon: Eye,
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    },
    {
      title: 'Overall CTR',
      value: `${analytics?.overall_ctr || '0'}%`,
      icon: MousePointer,
      color: 'text-orange-600',
      bg: 'bg-orange-50'
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading campaigns...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Campaign Management</h1>
            <p className="text-gray-600 mt-1">Create and manage marketing campaigns</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <Card key={stat.id || stat.name || `${stat.title || "stat"}-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold mt-2">{stat.value}</p>
                  </div>
                  <div className={`${stat.bg} p-3 rounded-lg`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {['all', 'active', 'draft', 'completed'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Campaigns List */}
        <div className="space-y-4">
          {campaigns.length > 0 ? (
            campaigns.map((campaign) => (
              <Card key={campaign.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{campaign.name}</h3>
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                          campaign.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                          campaign.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {campaign.status}
                        </span>
                        <span className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                          {campaign.campaign_type}
                        </span>
                      </div>
                      
                      {campaign.description && (
                        <p className="text-sm text-gray-600 mb-3">{campaign.description}</p>
                      )}

                      <div className="grid grid-cols-5 gap-4 mt-3">
                        <div>
                          <p className="text-xs text-gray-500">Budget</p>
                          <p className="font-semibold">₹{campaign.budget?.toLocaleString('en-IN')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Impressions</p>
                          <p className="font-semibold">{campaign.impressions?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Clicks</p>
                          <p className="font-semibold">{campaign.clicks?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">CTR</p>
                          <p className="font-semibold">{campaign.ctr || 0}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">ROI</p>
                          <p className={`font-semibold ${
                            campaign.roi > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {campaign.roi || 0}%
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                        <span>Start: {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString('en-IN') : 'Not set'}</span>
                        <span>End: {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString('en-IN') : 'Not set'}</span>
                        {campaign.days_remaining !== undefined && campaign.days_remaining > 0 && (
                          <span className="text-orange-600 font-medium">
                            {campaign.days_remaining} days remaining
                          </span>
                        )}
                        <span>{campaign.participating_vendors?.length || 0} vendors</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      {campaign.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateCampaignStatus(campaign.id, 'active')}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Launch
                        </Button>
                      )}
                      {campaign.status === 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateCampaignStatus(campaign.id, 'paused')}
                        >
                          <Pause className="w-4 h-4 mr-1" />
                          Pause
                        </Button>
                      )}
                      {campaign.status === 'paused' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateCampaignStatus(campaign.id, 'active')}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Resume
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteCampaign(campaign.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Target className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No campaigns found</h3>
                <p className="text-gray-600 mb-4">Create your first campaign to start marketing</p>
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Campaign
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Create Campaign Modal */}
        {showCreateModal && (
          <CreateCampaignModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchData();
            }}
            apiCall={apiCall}
            toast={toast}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

// Create Campaign Modal Component
const CreateCampaignModal = ({ onClose, onSuccess, apiCall, toast }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    campaign_type: 'banner',
    budget: '',
    start_date: '',
    end_date: '',
    banner_image_url: '',
    banner_link: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiCall('POST', '/campaigns', {
        ...formData,
        budget: parseFloat(formData.budget)
      });

      if (response.success) {
        toast({ title: 'Success', description: 'Campaign created successfully' });
        onSuccess();
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Create New Campaign</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Campaign Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Summer Sale 2025"
                required
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Campaign description..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Campaign Type *</Label>
                <select
                  value={formData.campaign_type}
                  onChange={(e) => setFormData({ ...formData, campaign_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="banner">Banner Ad</option>
                  <option value="email">Email Campaign</option>
                  <option value="promotion">Promotion</option>
                  <option value="featured">Featured Listing</option>
                </select>
              </div>

              <div>
                <Label>Budget (₹) *</Label>
                <Input
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  placeholder="50000"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Banner Image URL</Label>
              <Input
                value={formData.banner_image_url}
                onChange={(e) => setFormData({ ...formData, banner_image_url: e.target.value })}
                placeholder="https://example.com/banner.jpg"
              />
            </div>

            <div>
              <Label>Banner Link URL</Label>
              <Input
                value={formData.banner_link}
                onChange={(e) => setFormData({ ...formData, banner_link: e.target.value })}
                placeholder="https://example.com/products"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? 'Creating...' : 'Create Campaign'}
              </Button>
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCampaignDashboardNew;
